
import { Component, Vue } from "vue-property-decorator";
import { DimssaButton } from "@/components/shared/dimssa-button.vue";
import BreadcrumbTrail from "@/components/BreadcrumbTrail.vue";
import { formatDetailBreadcrumbLabel, setupDetailBreadcrumbs } from "@/utils/breadcrumbs";

interface BlendSessionMix {
  session: number;
  rationAPct?: number;
  rationBPct?: number;
}

interface BlendPlanDay {
  day: number;
  sessions: BlendSessionMix[];
}

interface BlendPlan {
  typename?: string;
  guid?: string;
  name: string;
  days?: number;
  kraalTypeGuid?: string | null;
  dayPlans: BlendPlanDay[];
  creationDate?: number;
}

@Component({
  components: {
    DimssaButton,
    BreadcrumbTrail,
  },
})
export default class BlendPlanView extends Vue {
  loading = false;
  changed = false;
  blendPlan: BlendPlan = { typename: "BlendPlan", guid: "", name: "", dayPlans: [] };
  kraalTypes: any[] = [];
  feedingStrategyItems: any[] = [];
  sessionCount = 0;
  selectedKraalTypeGuid: string | null = null;
  dayCount = 0;
  hasSavedSessions = false;

  get breadcrumbs() {
    const detailLabel = formatDetailBreadcrumbLabel("Blend Plan", this.blendPlan?.name);
    return setupDetailBreadcrumbs({
      listText: "Blend Plans",
      listRoute: "/blendPlans",
      currentText: detailLabel,
    });
  }

  mounted() {
    this.loadPlanFromStore();
    this.fetchStrategy();
    this.fetchKraalTypes();
  }

  loadPlanFromStore() {
    const stored = (this.$store.getters as any)["storage"]?.();
    if (stored && stored.blendPlan) {
      this.blendPlan = {
        ...stored.blendPlan,
        dayPlans: (stored.blendPlan.dayPlans || []).map((day: BlendPlanDay) => ({
          ...day,
          sessions: (day.sessions || []).map((session) => ({ ...session })),
        })),
      };
      this.dayCount = this.blendPlan.dayPlans?.length || this.blendPlan.days || 0;
    } else {
      this.blendPlan.guid = `${Date.now()}`;
      this.dayCount = 1;
    }
    this.selectedKraalTypeGuid = this.blendPlan.kraalTypeGuid || null;
    const sessions = this.blendPlan.dayPlans.flatMap((day) => day.sessions);
    this.hasSavedSessions = sessions.length > 0;
    this.syncDayPlans();
  }

  get sessionDefaultCount() {
    const storedCount = this.blendPlan.dayPlans?.reduce((max, dp) => Math.max(max, dp.sessions?.length || 0), 0) || 0;
    const strategyCount = this.feedingStrategyItems.length || 0;

    if (this.selectedKraalTypeGuid) {
      const kt = this.kraalTypes.find((k: any) => k.guid === this.selectedKraalTypeGuid);
      if (kt && kt.noOfSessions && kt.noOfSessions > 0) {
        return kt.noOfSessions;
      }
    }

    return this.hasSavedSessions ? storedCount || Math.max(strategyCount, 1) : Math.max(storedCount, strategyCount, 1);
  }

  getChoice(dayIndex: number, sessionNumber: number): "A" | "B" | null {
    const session = this.getSession(dayIndex, sessionNumber);
    if (!session) return null;
    if (session.rationAPct && session.rationAPct > 0) return "A";
    if (session.rationBPct && session.rationBPct > 0) return "B";
    return null;
  }

  getSession(dayIndex: number, sessionNumber: number): BlendSessionMix | undefined {
    return this.blendPlan.dayPlans[dayIndex]?.sessions?.find((s) => s.session === sessionNumber);
  }

  setChoice(dayIndex: number, sessionNumber: number, choice: "A" | "B") {
    this.changed = true;
    const day = this.blendPlan.dayPlans[dayIndex];
    if (!day) return;
    const session = this.getSession(dayIndex, sessionNumber);
    const rationAPct = choice === "A" ? 100 : 0;
    const rationBPct = choice === "B" ? 100 : 0;
    if (session) {
      session.rationAPct = rationAPct;
      session.rationBPct = rationBPct;
    }
  }

  getSessionLabel(dayIndex: number, sessionNumber: number) {
    const choice = this.getChoice(dayIndex, sessionNumber);
    if (!choice) return "Unassigned";
    return choice === "A" ? "Ration A" : "Ration B";
  }

  syncDayPlans() {
    const count = Math.max(1, this.dayCount || 1);
    if (!Array.isArray(this.blendPlan.dayPlans)) this.blendPlan.dayPlans = [];
    while (this.blendPlan.dayPlans.length < count) {
      const nextIndex = this.blendPlan.dayPlans.length;
      this.blendPlan.dayPlans.push({ day: nextIndex + 1, sessions: [] });
    }
    this.blendPlan.dayPlans = this.blendPlan.dayPlans.slice(0, count).map((dp, idx) => ({ ...dp, day: idx + 1 }));
    this.syncSessions();
  }

  syncSessions() {
    this.sessionCount = Math.max(1, this.sessionDefaultCount || 1);
    this.blendPlan.dayPlans.forEach((day) => {
      if (!Array.isArray(day.sessions)) day.sessions = [];
      while (day.sessions.length < this.sessionCount) {
        const sessionNum = day.sessions.length + 1;
        day.sessions.push({
          session: sessionNum,
          rationAPct: 0,
          rationBPct: 0,
        });
      }
      day.sessions = day.sessions.slice(0, this.sessionCount).map((s, idx) => ({ ...s, session: idx + 1 }));
    });
  }

  onDayCountChange() {
    if (this.dayCount < 1) this.dayCount = 1;
    this.syncDayPlans();
  }

  onKraalTypeChange() {
    this.changed = true;
    this.syncSessions();
  }

  async fetchStrategy() {
    try {
      const json = await this.$store.dispatch("graphQl", {
        gql: `query($guid:String!){ Strategy(guid:$guid){ FeedingPlanItems{ sequence } } }`,
        variables: { guid: this.$store.state.user.location.guid },
      });
      this.feedingStrategyItems = json.data.Strategy?.FeedingPlanItems ?? [];
      this.syncSessions();
    } catch (err) {
      console.log("fetchStrategy error", err);
    }
  }

  async fetchKraalTypes() {
    try {
      const json = await this.$store.dispatch("graphQl", {
        gql: `query($guid:String!){ KraalType(guid:$guid){ guid kraalType noOfSessions } }`,
        variables: { guid: this.$store.state.user.location.guid },
      });
      this.kraalTypes = json.data.KraalType ?? [];
      this.syncSessions();
    } catch (err) {
      console.log("fetchKraalTypes error", err);
    }
  }

  normalizeBlendPlan(): BlendPlan {
    const payload: BlendPlan = {
      ...this.blendPlan,
      guid: this.blendPlan.guid || `${Date.now()}`,
      typename: "BlendPlan",
      name: this.blendPlan.name,
      days: this.blendPlan.dayPlans.length,
      kraalTypeGuid: this.selectedKraalTypeGuid,
      dayPlans: this.blendPlan.dayPlans.map((dp, dIdx) => ({
        day: dIdx + 1,
        sessions: dp.sessions.slice(0, this.sessionCount).map((_, sIdx) => {
          const choice = this.getChoice(dIdx, sIdx + 1);
          return {
            session: sIdx + 1,
            rationAPct: choice === "A" ? 100 : 0,
            rationBPct: choice === "B" ? 100 : 0,
          };
        }),
      })),
    };
    return payload;
  }

  async save() {
    const payload = this.normalizeBlendPlan();
    try {
      await this.$store.dispatch("graphQl", {
        gql: `mutation blendPlan($guid:String!, $blendPlan: BlendPlanInput!){
          blendPlan(guid:$guid, blendPlan:$blendPlan)
        }`,
        variables: {
          guid: this.$store.state.user.location.guid,
          blendPlan: payload,
        },
      });
      this.changed = false;
      this.$router.go(-1);
    } catch (err) {
      console.log("save blend plan error", err);
    }
  }

  cancel() {
    if (this.changed) {
      this.$store.commit("popup/displayYesNo", {
        message: "Discard changes?",
        yesAction: () => this.$router.go(-1),
      });
    } else {
      this.$router.go(-1);
    }
  }
}
